import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, Briefcase, Loader2 } from 'lucide-react';
import { CrmContact, contactsApi } from '../services/contactsApi';
import { toast } from 'sonner';
interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: CrmContact | null; // If provided, we're editing; otherwise creating
  onSuccess?: () => void;
}
export function ContactModal({
  isOpen,
  onClose,
  contact,
  onSuccess
}: ContactModalProps) {
  const isEditing = !!contact;
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    First_Name: '',
    Last_Name: '',
    Email: '',
    Phone: '',
    Mobile: '',
    Title: '',
    Department: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (isOpen) {
      if (contact) {
        setFormData({
          First_Name: contact.First_Name || '',
          Last_Name: contact.Last_Name || '',
          Email: contact.Email || '',
          Phone: contact.Phone || '',
          Mobile: contact.Mobile || '',
          Title: contact.Title || '',
          Department: contact.Department || ''
        });
      } else {
        setFormData({
          First_Name: '',
          Last_Name: '',
          Email: '',
          Phone: '',
          Mobile: '',
          Title: '',
          Department: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, contact]);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.Last_Name.trim()) {
      newErrors.Last_Name = 'Last Name is required';
    }
    if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      newErrors.Email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      if (isEditing && contact) {
        await contactsApi.update(contact.id, formData);
        toast.success('Contact updated', {
          description: `${formData.First_Name} ${formData.Last_Name} has been updated in Zoho CRM.`
        });
      } else {
        await contactsApi.create(formData);
        toast.success('Contact created', {
          description: `${formData.First_Name} ${formData.Last_Name} has been added to Zoho CRM.`
        });
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save contact:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save contact.';
      toast.error(isEditing ? 'Failed to update contact' : 'Failed to create contact', {
        description: msg
      });
      setErrors(prev => ({
        ...prev,
        submit: msg
      }));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-667">
      <div className="flex min-h-screen items-center justify-center p-4" data-id="element-668">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} data-id="element-669" />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-670">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-671">
            <div className="flex items-center gap-3" data-id="element-672">
              <div className="p-2 bg-blue-50 rounded-lg" data-id="element-673">
                <User className="h-5 w-5 text-blue-600" data-id="element-674" />
              </div>
              <div data-id="element-675">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-676">
                  {isEditing ? 'Edit Contact' : 'Add New Contact'}
                </h2>
                <p className="text-sm text-gray-500" data-id="element-677">
                  {isEditing ? 'Update contact information in Zoho CRM' : 'Create a new contact in Zoho CRM'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 flex-shrink-0" data-id="element-678">
              <X className="h-5 w-5" data-id="element-679" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" data-id="element-680">
            <div className="px-6 py-4 space-y-6" data-id="element-681">
              {errors.submit && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm" data-id="element-682">
                  {errors.submit}
                </div>}

              {/* Basic Info */}
              <div className="space-y-4" data-id="element-683">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-684">
                  <Mail className="h-4 w-4 text-gray-400" data-id="element-685" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-686">
                  <div data-id="element-687">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-688">
                      First Name
                    </label>
                    <input type="text" value={formData.First_Name} onChange={e => setFormData(prev => ({
                    ...prev,
                    First_Name: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="John" data-id="element-689" />
                  </div>

                  <div data-id="element-690">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-691">
                      Last Name <span className="text-red-500" data-id="element-692">*</span>
                    </label>
                    <input type="text" value={formData.Last_Name} onChange={e => setFormData(prev => ({
                    ...prev,
                    Last_Name: e.target.value
                  }))} className={`block w-full rounded-md shadow-sm sm:text-sm ${errors.Last_Name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`} placeholder="Doe" data-id="element-693" />
                    {errors.Last_Name && <p className="mt-1 text-sm text-red-600" data-id="element-694">
                        {errors.Last_Name}
                      </p>}
                  </div>

                  <div className="sm:col-span-2" data-id="element-695">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-696">
                      Email Address
                    </label>
                    <input type="email" value={formData.Email} onChange={e => setFormData(prev => ({
                    ...prev,
                    Email: e.target.value
                  }))} className={`block w-full rounded-md shadow-sm sm:text-sm ${errors.Email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`} placeholder="contact@example.com" data-id="element-697" />
                    {errors.Email && <p className="mt-1 text-sm text-red-600" data-id="element-698">
                        {errors.Email}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="space-y-4" data-id="element-699">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-700">
                  <Phone className="h-4 w-4 text-gray-400" data-id="element-701" />
                  Contact Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-702">
                  <div data-id="element-703">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-704">
                      Phone
                    </label>
                    <input type="text" value={formData.Phone} onChange={e => setFormData(prev => ({
                    ...prev,
                    Phone: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="+1 234 567 8900" data-id="element-705" />
                  </div>

                  <div data-id="element-706">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-707">
                      Mobile
                    </label>
                    <input type="text" value={formData.Mobile} onChange={e => setFormData(prev => ({
                    ...prev,
                    Mobile: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="+1 234 567 8900" data-id="element-708" />
                  </div>
                </div>
              </div>

              {/* Professional Info */}
              <div className="space-y-4" data-id="element-709">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-710">
                  <Briefcase className="h-4 w-4 text-gray-400" data-id="element-711" />
                  Professional Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-712">
                  <div data-id="element-713">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-714">
                      Title
                    </label>
                    <input type="text" value={formData.Title} onChange={e => setFormData(prev => ({
                    ...prev,
                    Title: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Manager" data-id="element-715" />
                  </div>

                  <div data-id="element-716">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-717">
                      Department
                    </label>
                    <input type="text" value={formData.Department} onChange={e => setFormData(prev => ({
                    ...prev,
                    Department: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Sales" data-id="element-718" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-719">
              <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap disabled:opacity-50" data-id="element-720">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap disabled:opacity-50 flex items-center gap-2" data-id="element-721">
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-722" />}
                {isEditing ? 'Save Changes' : 'Create Contact'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}